import { useState, useEffect } from "react";
import {
  Box, Typography, TextField, InputAdornment,
  CircularProgress, Card, CardActionArea, CardContent,
  Chip, Alert, Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import MicrosoftIcon from "@mui/icons-material/Window";
import { MvAdminShadowOrganizations, MvAdminShadowToken, MvAdminAuthorizeUrl } from "../api/endpoints";
import { toastErrorNotify, successNote } from "../helpers/toastNotify";
import apiClient from "../api/apiClient";
import getCookie from "../utils/cookieUtils";
import Header from "../layout/Header";

/** Decode the type claim from the current JWT cookie. */
function getTokenType() {
  try {
    const token = getCookie("token");
    if (!token) return null;
    const raw = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = raw + "=".repeat((4 - (raw.length % 4)) % 4);
    return JSON.parse(atob(padded))?.type ?? null;
  } catch {
    return null;
  }
}

/**
 * "Besøk kunde" page — accessible from the sidebar for @makingview.com super/assistant admins.
 *
 * Two states:
 *  1. Not yet signed in as MV admin → shows "Sign in with Microsoft" button.
 *  2. Signed in as MV (type="mv" JWT) → shows org picker. Selecting an org issues a
 *     30-min shadow token, swaps the cookie, and redirects to the dashboard.
 */
export default function ShadowOrgPickerPage() {
  const [orgs, setOrgs]           = useState([]);
  const [filtered, setFiltered]   = useState([]);
  const [search, setSearch]       = useState("");
  const [loading, setLoading]     = useState(false);
  const [entering, setEntering]   = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [signingIn, setSigningIn] = useState(false);

  // Determine whether the current JWT is an MV session token.
  const isMvSession = getTokenType() === "mv";

  // ── Consume the MV session token placed in URL hash by the backend callback ──
  useEffect(() => {
    const match = window.location.hash.match(/[#&]token=([^&]+)/);
    if (!match) return;

    const token = decodeURIComponent(match[1]);
    document.cookie = `token=${token}; path=/; SameSite=Strict; Secure`;
    localStorage.setItem("isLoggedIn", "true");
    localStorage.setItem("roles", JSON.stringify(["org.admin.super", "org.admin.assistant"]));
    // Clean the token from the URL.
    window.history.replaceState(null, "", window.location.pathname);
    // Force a re-render now that the cookie is set.
    window.location.reload();
  }, []);

  // ── Fetch org list once we have an MV session ────────────────────────────
  useEffect(() => {
    if (!isMvSession) return;

    setLoading(true);
    apiClient.get(MvAdminShadowOrganizations)
      .then(res => {
        const data = res.data?.data ?? [];
        setOrgs(data);
        setFiltered(data);
      })
      .catch(() => setFetchError("Klarte ikke hente organisasjonsliste. Prøv å logge inn på nytt."))
      .finally(() => setLoading(false));
  }, [isMvSession]);

  // ── Filter ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const q = search.toLowerCase().trim();
    setFiltered(q ? orgs.filter(o => o.orgName.toLowerCase().includes(q)) : orgs);
  }, [search, orgs]);

  // ── Entra login ───────────────────────────────────────────────────────────
  const handleMicrosoftLogin = async () => {
    setSigningIn(true);
    try {
      const res = await apiClient.get(MvAdminAuthorizeUrl);
      const url = res.data?.authorizeUrl;
      if (url) window.location.href = url;
      else toastErrorNotify("Klarte ikke hente innloggingslenke.");
    } catch {
      toastErrorNotify("Klarte ikke koble til innloggingstjenesten.");
    } finally {
      setSigningIn(false);
    }
  };

  // ── Enter shadow session ──────────────────────────────────────────────────
  const handleSelectOrg = async (org) => {
    setEntering(org.orgId);
    try {
      const res = await apiClient.post(MvAdminShadowToken, { orgId: org.orgId });
      const shadowToken = res.data?.data?.token;
      if (!shadowToken) throw new Error("Ingen token i respons");

      document.cookie = `token=${shadowToken}; path=/; SameSite=Strict; Secure`;
      sessionStorage.setItem("shadowOrgName", org.orgName);
      // Restore normal roles so RequireAuth passes.
      localStorage.setItem("roles", JSON.stringify(["org.admin.super"]));

      successNote(`Skyggemodus aktivert for ${org.orgName}`);
      window.location.href = "/";
    } catch {
      toastErrorNotify("Klarte ikke opprette skyggesesjon. Prøv igjen.");
      setEntering(null);
    }
  };

  // ── Render: not yet an MV session ─────────────────────────────────────────
  if (!isMvSession) {
    return (
      <Box sx={{ p: 4 }}>
        <Header header="Besøk kunde" headerSize="h6" />
        <Box sx={{ mt: 4, maxWidth: 480 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Logg inn med din Making View Microsoft-konto for å velge en
            kundeorganisasjon å besøke. All personinformasjon vil bli
            anonymisert under besøket.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={signingIn ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <MicrosoftIcon />}
            onClick={handleMicrosoftLogin}
            disabled={signingIn}
            sx={{ backgroundColor: "#2f4874", "&:hover": { backgroundColor: "#1e3255" } }}
          >
            {signingIn ? "Logger inn…" : "Logg inn med Microsoft"}
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Render: MV session active — show org picker ───────────────────────────
  return (
    <Box sx={{ p: 4 }}>
      <Header header="Besøk kunde" headerSize="h6" />

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
        Velg en kundeorganisasjon å besøke. All personinformasjon vil vises anonymisert.
      </Typography>

      <TextField
        fullWidth
        placeholder="Søk etter organisasjon…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "text.secondary" }} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3, maxWidth: 480, backgroundColor: "#fff", borderRadius: 1 }}
      />

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "flex-start", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {fetchError && <Alert severity="error" sx={{ mb: 2, maxWidth: 480 }}>{fetchError}</Alert>}

      {!loading && !fetchError && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 2,
            maxWidth: 900,
          }}
        >
          {filtered.map(org => (
            <Card
              key={org.orgId}
              elevation={1}
              sx={{
                opacity: org.isActive ? 1 : 0.55,
                border: "1px solid #e0e0e0",
                transition: "box-shadow 0.15s",
                "&:hover": { boxShadow: 4 },
              }}
            >
              <CardActionArea
                onClick={() => handleSelectOrg(org)}
                disabled={!!entering}
                sx={{ p: 0 }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <BusinessIcon sx={{ color: "#5177b8", mt: 0.25, flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap title={org.orgName}>
                        {org.orgName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }} noWrap>
                        {org.orgId}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.5, mt: 1, alignItems: "center" }}>
                        <Chip
                          label={org.isActive ? "Aktiv" : "Inaktiv"}
                          size="small"
                          sx={{
                            backgroundColor: org.isActive ? "#e8f5e9" : "#eeeeee",
                            color: org.isActive ? "#2e7d32" : "#757575",
                            fontSize: 11,
                          }}
                        />
                        {entering === org.orgId && (
                          <CircularProgress size={14} sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}

          {filtered.length === 0 && (
            <Typography color="text.secondary" sx={{ gridColumn: "1 / -1", py: 4 }}>
              Ingen organisasjoner matcher søket.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
